/* eslint-disable max-lines */
import {
    Box,
    Button,
    Divider,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerOverlay,
    Flex,
    HStack,
    Heading,
    Skeleton,
    Text,
    VStack,
    useDisclosure,
} from '@chakra-ui/react';
import React, {
    ComponentProps,
    ReactElement, useContext, useEffect, useMemo,
} from 'react';
import { Hide, Show, SkeletonMotion } from '../../components';
import { ThemeTokens } from '../../theme/tokens';

const styles = {
    border: '1px',
    borderColor: ThemeTokens.Defaults.borderColor,
    bgColor: 'white',
    px: ThemeTokens.Defaults.px,
    py: ThemeTokens.Defaults.py,
};

type PageLayoutContextValues = {
    isSidebarOpen: boolean;
    onSidebarOpen: () => void;
    onSidebarClose: () => void;
};

const PageLayoutContext = React.createContext<PageLayoutContextValues>({
    isSidebarOpen: false,
    onSidebarOpen: () => {},
    onSidebarClose: () => {},
});

function usePageLayoutContext() {
    const ctx = useContext(PageLayoutContext);

    if (ctx === undefined) {
        throw new Error(
            'Can not use usePageLayoutContext outside of PageLayoutContextProvider'
        );
    }

    return ctx;
}

export function PageLayout({ children }: { children?: React.ReactNode }) {
    const sidebarDisclosure = useDisclosure();
    const contextProps = useMemo(
        () => ({
            isSidebarOpen: sidebarDisclosure.isOpen,
            onSidebarOpen: sidebarDisclosure.onOpen,
            onSidebarClose: sidebarDisclosure.onClose,
        }),
        [
            sidebarDisclosure.onClose,
            sidebarDisclosure.onOpen,
            sidebarDisclosure.isOpen,
        ]
    );

    return (
        <Box justifyContent="initial" w="full" flexGrow="1">
            <PageLayoutContext.Provider value={contextProps}>
                <Flex>{children}</Flex>
            </PageLayoutContext.Provider>
        </Box>
    );
}

function SidebarDrawer({ children }: { children?: React.ReactNode }) {
    const ctx = usePageLayoutContext();

    useEffect(() => () => ctx.onSidebarClose(), []);

    return (
        <Drawer
            isOpen={ctx.isSidebarOpen}
            placement="left"
            onClose={ctx.onSidebarClose}
            size={{ base: 'full', sm: 'sm' }}
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerBody
                    px={styles.px}
                    py={styles.py}
                    bgColor={styles.bgColor}
                    minW="fit-content"
                >
                    {children}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

function Sidebar({ children }: { children?: React.ReactNode }) {
    return (
        <>
            <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <SidebarDrawer>{children}</SidebarDrawer>
            </Hide>
            <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <Box
                    minH="100vh"
                    bgColor={styles.bgColor}
                    px={styles.px}
                    py={styles.py}
                    minW="80"
                    maxW="96"
                    w="fit-content"
                    borderRight={styles.border}
                    borderColor={styles.borderColor}
                >
                    {children}
                </Box>
            </Show>
        </>
    );
}

function SidebarDivider() {
    return (
        <Box py={6}>
            <Divider />
        </Box>
    );
}

function SidebarSection(
    {
        title,
        children,
        ...props
    }: {
        title: string;
        children: React.ReactNode;
    } & ComponentProps<typeof VStack>
) {
    return (
        <VStack alignItems="stretch" w="100%" {...props}>
            <Box>
                <Heading size="md">{title}</Heading>
            </Box>
            { children }
        </VStack>
    );
}

function Content({ children }: { children?: React.ReactNode }) {
    return (
        <VStack w="full" spacing={0}>
            {children}
        </VStack>
    );
}

function Controls({ children }: { children?: React.ReactNode }) {
    return (
        <Box
            px={styles.px}
            py={[3, null, 4]}
            w="full"
            bgColor={styles.bgColor}
            borderBottom={styles.border}
            borderColor={styles.borderColor}
        >
            {children}
        </Box>
    );
}

function TitleBreadcrumbs({ children }: { children?: React.ReactNode }) {
    return (
        <Box color="gray.500" fontSize="sm" mb={3}>
            {children}
        </Box>
    );
}

function TitleAside(
    {
        children,
        mobileAside = false,
    }: {
        mobileAside?: boolean;
        children?: React.ReactNode;
    }
) {
    return (
        <Box
            w={mobileAside ? 'auto' : ['full', null, 'auto']}
            borderTop={mobileAside ? 0 : ['1px', null, 0]}
            borderTopColor={styles.borderColor}
            py={mobileAside ? 0 : [3, null, 0]}
            pr={styles.px}
            pl={mobileAside ? 0 : [5, null, 0]}
        >
            {children}
        </Box>
    );
}

function TitleHeader({
    children,
}: {
    children?: React.ReactNode;
}) {
    return (
        <Heading
            as="h1"
            size="xmd"
            mb={1}
        >
            {children}
        </Heading>
    );
}

function TitleDescription({
    children,
}: {
    children?: React.ReactNode;
}) {
    return (
        <Text as="div" color="muted.800" fontSize={['medium', null, 'large']}>
            {children}
        </Text>
    );
}

function TitleSkeleton({
    breadcrumbs = false,
}: {
    breadcrumbs?: boolean;
}) {
    return (
        <>
            {
                breadcrumbs && (
                    <PageLayout.Title.Breadcrumbs>
                        <Skeleton opacity={0.3} sx={{ animationDuration: '2s' }}>
                            Breadcrumb Breadcrumb
                        </Skeleton>
                    </PageLayout.Title.Breadcrumbs>
                )
            }
            <PageLayout.Title.Header>
                <Skeleton sx={{ animationDuration: '2s' }}>
                    Loading really long page title...
                </Skeleton>
            </PageLayout.Title.Header>
            <PageLayout.Title.Description>
                <Box width="96">
                    <SkeletonMotion fontSize="sm" mb={1} opacity={0.4} sx={{ animationDuration: '2s' }} h={5} randMul={20} timeout={1500} />
                </Box>
            </PageLayout.Title.Description>
        </>
    );
}

function Title({
    children,
    mobileAside = false,
}: {
    children?: React.ReactNode;
    mobileAside?: boolean;
}) {
    return (
        <Flex
            w="full"
            bgColor={styles.bgColor}
            borderBottom={styles.border}
            borderColor={styles.borderColor}
            direction={mobileAside ? 'row' : ['column', null, 'row']}
            justifyContent="space-between"
            alignItems={mobileAside ? 'center' : ['stretch', null, 'center']}
            gap={mobileAside ? [2, null, 4] : [0, null, 4]}
        >
            <VStack
                alignItems="start"
                gap={0}
                flexGrow="1"
                px={styles.px}
                py={styles.py}
            >
                {/**
                 * This little bit of nastiness lets us pick up components
                 * from children and place them in different spots
                 * based on their defined types
                */}
                {React.Children.toArray(children)
                    .filter((child) => (child as ReactElement).type === TitleBreadcrumbs)}
                {React.Children.toArray(children)
                    .filter((child) => (child as ReactElement).type === TitleHeader)}
                {React.Children.toArray(children)
                    .filter((child) => (child as ReactElement).type === TitleDescription)}
                {React.Children.toArray(children)
                    .filter((child) => (child as ReactElement).type === TitleSkeleton)}
            </VStack>
            {React.Children.toArray(children)
                .filter((child) => (child as ReactElement).type !== TitleBreadcrumbs
                    && (child as ReactElement).type !== TitleHeader
                    && (child as ReactElement).type !== TitleDescription
                    && (child as ReactElement).type !== TitleSkeleton)}
        </Flex>
    );
}

function Body({ children }: { children?: React.ReactNode }) {
    return (
        <Box px={styles.px} py={styles.py} w="full">
            {children}
        </Box>
    );
}

// eslint-disable-next-line react/prop-types
function SidebarButton({ onClick, ...props }: React.ComponentProps<typeof Button>) {
    const ctx = usePageLayoutContext();

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        ctx.onSidebarOpen();
        onClick?.(e);
    };

    return (<Button onClick={handleClick} {...props} />);
}

function SidebarSkeleton() {
    return (
        <VStack gap={0} alignItems="start">
            <Skeleton mb={2} sx={{ animationDuration: '1.8s' }}>
                <Heading
                    fontSize="1.7rem"
                    lineHeight={1}
                >
                    Loading page title
                </Heading>
            </Skeleton>
            <Skeleton mb={10} opacity={0.5} sx={{ animationDuration: '1.8s' }}>
                <Text fontSize="small">
                    Loading page description...
                </Text>
            </Skeleton>
            {[
                'Longer item',
                'Short',
                'Slightly longer',
                'Bigger short',
                'Short',
                'Slightly longer',
                'Longer item',
                'Longer item',
                'Smaller',
                'Shorter item',
                'Random string',
                'Slightly longer',
            ].map((_x, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <SkeletonMotion key={idx} mb={4} opacity={0.3} sx={{ animationDuration: `${1.2 + idx * 0.1}s` }} height={5} />
            ))}
        </VStack>
    );
}

function BodySkeleton() {
    return (
        <VStack alignItems="start" opacity={0.25}>
            <Skeleton height={36} mb={8} width="100%" sx={{ animationDuration: '2s' }} />
            <Skeleton height={28} mb={3} width="100%" sx={{ animationDuration: '2.2s' }} />
            <Skeleton height={28} mb={3} width="100%" sx={{ animationDuration: '2.4s' }} />
        </VStack>
    );
}

function ControlsSkeleton() {
    return (
        <HStack alignItems="start">
            <Skeleton opacity={0.4} sx={{ animationDuration: '1.8s' }}>
                <Button size="sm">call to action</Button>
            </Skeleton>
            <Skeleton opacity={0.3} sx={{ animationDuration: '1.8s' }}>
                <Button size="sm">longer call to action</Button>
            </Skeleton>

            <Show above="md">
                <Skeleton opacity={0.3} ml="auto" sx={{ animationDuration: '1.8s' }}>
                    <Button size="sm">another call to action</Button>
                </Skeleton>
            </Show>
        </HStack>
    );
}

function TitleAsideSkeleton() {
    return (
        <VStack alignItems="start">
            <Skeleton sx={{ animationDuration: '2s' }}>
                <Button size={TitleAside.Size}>Call to action</Button>
            </Skeleton>
        </VStack>
    );
}

function PageLayoutSkeleton({
    sidebar = false,
    aside = false,
    breadcrumbs = false,
    controls = false,
}: {
    sidebar?: boolean;
    aside?: boolean;
    breadcrumbs?: boolean;
    controls?: boolean;
}) {
    return (
        <PageLayout>
            {sidebar && (
                <PageLayout.Sidebar>
                    <PageLayout.Sidebar.Skeleton />
                </PageLayout.Sidebar>
            )}
            <PageLayout.Content>
                <PageLayout.Title>
                    <PageLayout.Title.Skeleton breadcrumbs={breadcrumbs} />
                    {
                        aside && (
                            <PageLayout.Title.Aside>
                                <PageLayout.Title.Aside.Skeleton />
                            </PageLayout.Title.Aside>
                        )
                    }
                </PageLayout.Title>
                {
                    controls && (
                        <PageLayout.Controls>
                            <PageLayout.Controls.Skeleton />
                        </PageLayout.Controls>
                    )
                }
                <PageLayout.Body>
                    <PageLayout.Body.Skeleton />
                </PageLayout.Body>
            </PageLayout.Content>
        </PageLayout>
    );
}

Body.Skeleton = BodySkeleton;
Controls.Skeleton = ControlsSkeleton;
TitleAside.Size = ['sm', null, 'cta-md'];
TitleAside.Skeleton = TitleAsideSkeleton;

Sidebar.Button = SidebarButton;
Sidebar.Breakpoint = ThemeTokens.Defaults.sidebarBreakpoint;
Sidebar.Skeleton = SidebarSkeleton;

Title.Aside = TitleAside;
Title.Breadcrumbs = TitleBreadcrumbs;
Title.Header = TitleHeader;
Title.Description = TitleDescription;
Title.Skeleton = TitleSkeleton;

Sidebar.Divider = SidebarDivider;
Sidebar.Section = SidebarSection;

PageLayout.Sidebar = Sidebar;
PageLayout.Title = Title;
PageLayout.Controls = Controls;
PageLayout.Content = Content;
PageLayout.Body = Body;
PageLayout.Skeleton = PageLayoutSkeleton;
